import {
  selectBuildingsDamage,
  selectHexes,
  selectPlayers,
  selectSpecialBuildings,
  selectUnits,
} from "../redux/reducers/sharedStateReducer/selectors";
import {selectConstructionPlanEntries} from "../redux/reducers/privateStateReducer/selectors";
import {calculateCitiesCache} from "../utils/gameState/calculateCitiesCache";
import {findOwner} from "../utils/gameState/findOwner";
import {buildSupplyPathContext, findFarmSupplyNetwork} from "../utils/gameState/supply/findSupplyPaths";
import {
  checkIfConstructionPlanningAvailable,
  getConstructionPlanPlayer,
} from "../utils/gameState/construction/planning/getConstructionPlanContext";
import {selectFieldWithConstructionPlan} from "../utils/gameState/construction/planning/selectFieldWithConstructionPlan";
import {COMMON_BUILDING_TOKENS} from "../constants/tokens";
import {POLICIES} from "../constants/policies";
import {isPlayerPolicy} from "../utils/factions/isPlayerPolicy";
import {REGISTRY_SINGLETON} from "./Registry";

// Задержка проигрывания — та же, что у соседних разовых анимаций постройки
// (playCityGrowthAnimation, playNearbyGuildsTemplesIncomeChangeAnimation):
// точки сети диаметром с ячейку иначе накрывают только что поставленный жетон
// прежде, чем игрок успеет его разглядеть
const FARM_SUPPLY_NETWORK_DELAY_MS = 1000;

// Ключ анимации сети осматриваемой фермы — один на все ячейки: сеть новой
// осмотренной фермы сменяет прежнюю, а не ложится поверх нее (сети ферм одного
// города почти совпадают). С ключами построек (ключ ячейки) не пересекается
export const INSPECTED_FARM_SUPPLY_NETWORK_KEY = 'inspected-farm';

// Город построенной фермы: у города нет своего id, ищем по ячейке
const findCityOwnerColor = (citiesCache, hexKey) => {
  for (const [playerColor, cities] of Object.entries(citiesCache || {})) {
    const hasHex = (cities || []).some((city) => Object.values(city)
      .some((value) => Array.isArray(value) && value.includes(hexKey)));
    if (hasHex) {
      return playerColor;
    }
  }
  return null;
};

/**
 * Сеть связей снабжения фермы от лица ее владельца на заданном поле (см.
 * findFarmSupplyNetwork) либо null, если показывать нечего: владельца нет,
 * ферма не работает или до других ферм от нее не дотянуться.
 *
 * @param {Object} state
 * @param {Object} hexGridRenderer
 * @param {string} farmHexKey
 * @param {Object} [owner] - владелец фермы
 * @param {Object} field - поле, по которому считается сеть: настоящее или
 *   «с планом» (selectFieldWithConstructionPlan)
 * @param {Object} field.hexes
 * @param {Object} field.specialBuildings
 * @param {Object} field.citiesCache - кэш городов ЭТОГО поля
 */
const findOwnerFarmSupplyNetwork = (state, hexGridRenderer, farmHexKey, owner, {hexes, specialBuildings, citiesCache}) => {
  if (!owner) return null;

  // Экоанархизм: ферма снабжает любой свой отряд без путей — связей между
  // фермами у такого игрока нет (как и анимации пути в фазе снабжения)
  if (isPlayerPolicy(owner, POLICIES.ECOANARCHISM)) return null;

  const network = findFarmSupplyNetwork(farmHexKey, buildSupplyPathContext({
    fieldHexes: hexGridRenderer.hexes,
    hexes,
    units: selectUnits(state),
    currentPlayer: owner,
    currentPlayerCities: citiesCache[owner.color] || [],
    specialBuildings,
    buildingsDamage: selectBuildingsDamage(state),
  }));

  return network.links.length > 0 ? network : null;
};

const showFarmSupplyNetwork = (hexGridRenderer, trackingKey, network) => {
  hexGridRenderer.showSupplyNetworkAnimation(
    trackingKey,
    network.links.map((link) => link.path),
    {nodeHexKeys: network.farmHexKeys}
  );
};

/**
 * После постройки фермы показывает ВСЕМ участникам матча сеть снабжения ее
 * владельца: связи от новой фермы ко всем фермам, до которых от нее дотягивается
 * маршрут снабжения, и дальше рекурсивно — но уже без ферм, вошедших в дерево
 * (см. findFarmSupplyNetwork). Стиль связей тот же, что у пути снабжения от
 * фермы до отряда.
 *
 * Зовется из общей воронки эффектов постройки (playBuildingPlacementEffects):
 * у строящего — из GameScene, у остальных игроков, зрителя, в записи матча и у
 * ботов — из playEffectForServerAction.
 *
 * Состояние читается СИНХРОННО сразу по возврату из dispatch (как у соседних
 * анимаций): отложен только момент проигрывания, иначе вторая постройка за
 * секунду задержки попала бы в расчет первой.
 *
 * @param store
 * @param hexGridRenderer
 * @param {string} placedHexKey
 * @param {Object} hexesBefore - hexes ДО диспатча размещения
 * @param {Object} [citiesCaches] - пара кэшей городов «до/после» (см.
 *   playBuildingPlacementEffects); без нее считается здесь же
 */
export const playFarmSupplyNetworkAnimation = (store, hexGridRenderer, placedHexKey, hexesBefore, citiesCaches) => {
  if (!hexGridRenderer || !hexesBefore) return;

  const state = store.getState();
  const hexesAfter = selectHexes(state);

  // Построена именно ферма и именно сейчас: экшен мог быть отклонен редьюсером
  // (не хватило монет или действий), а эффект все равно вызывается
  if (hexesAfter[placedHexKey] !== COMMON_BUILDING_TOKENS.FARM
    || hexesBefore[placedHexKey] === COMMON_BUILDING_TOKENS.FARM) {
    return;
  }

  const specialBuildings = selectSpecialBuildings(state);
  const citiesCacheAfter = citiesCaches
    ? citiesCaches.citiesCacheAfter : calculateCitiesCache(hexesAfter, specialBuildings);

  // Владелец — по городу ячейки, а не по ходящему игроку: у зрителя и в записи
  // матча ходящий не обязательно тот, кто строит
  const ownerColor = findCityOwnerColor(citiesCacheAfter, placedHexKey);
  const owner = (selectPlayers(state) || []).find((player) => player.color === ownerColor);

  const network = findOwnerFarmSupplyNetwork(state, hexGridRenderer, placedHexKey, owner, {
    hexes: hexesAfter,
    specialBuildings,
    citiesCache: citiesCacheAfter,
  });
  if (!network) return;

  setTimeout(() => {
    // Постройку могли снять двойным кликом за эту секунду
    if (selectHexes(store.getState())[placedHexKey] !== COMMON_BUILDING_TOKENS.FARM) {
      return;
    }
    // Тот же звук, что у раздачи снабжения от фермы отряду: связи рисуются
    // тем же стилем и означают то же самое движение снабжения
    REGISTRY_SINGLETON.getSoundManager()?.playDistributeSupply();
    showFarmSupplyNetwork(hexGridRenderer, placedHexKey, network);
  }, FARM_SUPPLY_NETWORK_DELAY_MS);
};

// Запись запланированной на ячейке фермы, пока идет планирование. Нужна сама
// запись, а не признак «ферма в плане есть»: ферму могли снять с плана и тут же
// поставить заново — это уже другая постановка со своей анимацией
const findPlannedFarmEntry = (state, hexKey) => (
  checkIfConstructionPlanningAvailable(state)
    ? selectConstructionPlanEntries(state)
      .filter((entry) => entry.hexKey === hexKey && entry.token === COMMON_BUILDING_TOKENS.FARM)
      .pop()
    : undefined
);

// Сеть, посчитанная раньше, все еще стоит на поле: фермы на месте, а ячейки, по
// которым идут связи, не опустели
const checkIfNetworkStandsOnField = (network, hexes) => (
  network.farmHexKeys.every((hexKey) => hexes[hexKey] === COMMON_BUILDING_TOKENS.FARM)
  && network.links.every((link) => link.path.every((hexKey) => hexes[hexKey]))
);

/**
 * То же для фермы, поставленной в ПЛАН строительства (игрок размечает свой
 * будущий ход, пока идет чужой): сеть снабжения самого планирующего на поле
 * «с планом» — запланированные фермы в ней такие же узлы, как построенные, а
 * запланированные дороги и кварталы проводят снабжение. Видит ее только сам
 * планирующий: план живет на его клиенте.
 *
 * Запланированной фермы нет в hexes — все сверки идут по плану. Сеть считается
 * сразу после постановки, показ отложен, как у настоящей постройки. К показу
 * планирование могло закончиться, а с плана — уйти сама ферма или то, с чем
 * она связана (двойной клик, отмена, чужая стройка): тогда показывать нечего.
 *
 * @param store
 * @param hexGridRenderer
 * @param {string} hexKey
 * @param {string} token - запланированный жетон
 */
export const playPlannedFarmSupplyNetworkAnimation = (store, hexGridRenderer, hexKey, token) => {
  if (!hexGridRenderer || token !== COMMON_BUILDING_TOKENS.FARM) return;

  const state = store.getState();
  const plannedEntry = findPlannedFarmEntry(state, hexKey);
  if (!plannedEntry) return;

  // Владелец — сам планирующий, а не город ячейки: ферма могла встать рядом с
  // кварталами двух игроков
  const network = findOwnerFarmSupplyNetwork(
    state, hexGridRenderer, hexKey, getConstructionPlanPlayer(state), selectFieldWithConstructionPlan(state)
  );
  if (!network) return;

  setTimeout(() => {
    const stateAtReveal = store.getState();
    if (findPlannedFarmEntry(stateAtReveal, hexKey) !== plannedEntry
      || !checkIfNetworkStandsOnField(network, selectFieldWithConstructionPlan(stateAtReveal).hexes)) {
      return;
    }
    REGISTRY_SINGLETON.getSoundManager()?.playDistributeSupply();
    showFarmSupplyNetwork(hexGridRenderer, hexKey, network);
  }, FARM_SUPPLY_NETWORK_DELAY_MS);
};

/**
 * Та же сеть — по клику осмотра ячейки с фермой: связи фермы видны не только в
 * миг постройки. Играет сразу и без звука раздачи: жетон давно на поле, а звук
 * выбора ячейки уже прозвучал (как у разовой анимации дохода гильдии и храма
 * при их осмотре).
 *
 * Сеть считается от лица владельца фермы, чья бы она ни была, и по полю таким,
 * каким оно нарисовано (selectFieldWithConstructionPlan): при планировании —
 * вместе с запланированным, поэтому осмотр показывает и связи запланированной
 * фермы, и связи построенной с запланированными.
 *
 * Зовется на КАЖДЫЙ осмотр ячейки: сеть прежней осмотренной фермы гаснет, даже
 * если у новой ячейки показывать нечего, — иначе чужие связи читались бы как ее.
 *
 * @param store
 * @param hexGridRenderer
 * @param {string} hexKey - осматриваемая ячейка
 */
export const playInspectedFarmSupplyNetworkAnimation = (store, hexGridRenderer, hexKey) => {
  if (!hexGridRenderer) return;

  hexGridRenderer.destroySupplyNetworkAnimation(INSPECTED_FARM_SUPPLY_NETWORK_KEY);

  const state = store.getState();
  const field = selectFieldWithConstructionPlan(state);
  if (field.hexes[hexKey] !== COMMON_BUILDING_TOKENS.FARM) return;

  // Кэш городов стора пересчитывается только на завершении хода строительства
  // и построенного в текущем ходу не видит — без плана на поле считаем заново
  const citiesCache = field.citiesCache || calculateCitiesCache(field.hexes, field.specialBuildings);
  const owner = findOwner(hexKey, selectPlayers(state) || [], citiesCache);
  const network = findOwnerFarmSupplyNetwork(state, hexGridRenderer, hexKey, owner, {...field, citiesCache});
  if (!network) return;

  showFarmSupplyNetwork(hexGridRenderer, INSPECTED_FARM_SUPPLY_NETWORK_KEY, network);
};
